/** Funcțiile tipizate de acces la API, grupate pe domenii. */
import { api } from './client';
import type {
  BalanceResponse,
  BillingProfile,
  BusinessProfile,
  Campaign,
  CatalogSku,
  Character,
  Clip,
  ClipDetails,
  FileItem,
  InterviewSession,
  Me,
  MusicTrack,
  NotificationItem,
  Order,
  Paginated,
  Product,
  PromoValidation,
  PublicClip,
  PronunciationSuggestResponse,
  SessionInfo,
  ShareLink,
  SupportMessage,
  SupportThread,
  Template,
  UltraJob,
  UltraMessage,
  VersionInfo,
  Voice,
} from './types';

// --- Sesiune / cont ---
export const getMe = () => api.get<Me>('/api/v1/me');
export const getSessions = () => api.get<SessionInfo[]>('/api/v1/me/sessions');
export const revokeSession = (id: string) => api.del(`/api/v1/me/sessions/${id}`);
export const getVersion = () => api.get<VersionInfo>('/api/version');

// --- Sold ---
export const getBalance = (page?: number, pageSize?: number) =>
  api.get<BalanceResponse>('/api/v1/me/balance', { page, pageSize });

// --- Catalog / comenzi ---
export const getCatalog = () => api.get<{ items: CatalogSku[] }>('/api/v1/catalog');
export const createOrder = (body: { skuId: string; promoCode?: string; billingProfileId?: string }) =>
  api.post<{ order: Order; checkoutUrl: string | null }>('/api/v1/orders', body);
export const validatePromo = (body: { skuId: string; code: string }) =>
  api.post<PromoValidation>('/api/v1/orders/validate-promo', body);
export const getMyOrders = (query?: { status?: string; page?: number; pageSize?: number }) =>
  api.get<Paginated<Order>>('/api/v1/me/orders', query);
export const getOrder = (id: string) => api.get<Order>(`/api/v1/me/orders/${id}`);

// --- Facturare ---
export const getBillingProfiles = () => api.get<{ items: BillingProfile[] }>('/api/v1/me/billing-profiles');
export const createBillingProfile = (body: Partial<BillingProfile>) =>
  api.post<BillingProfile>('/api/v1/me/billing-profiles', body);
export const updateBillingProfile = (id: string, body: Partial<BillingProfile>) =>
  api.patch<BillingProfile>(`/api/v1/me/billing-profiles/${id}`, body);
export const deleteBillingProfile = (id: string) => api.del(`/api/v1/me/billing-profiles/${id}`);

// --- Profil afacere ---
export const getBusinessProfile = () => api.get<BusinessProfile>('/api/v1/me/business-profile');
export const updateBusinessProfile = (body: Partial<BusinessProfile>) =>
  api.put<BusinessProfile>('/api/v1/me/business-profile', body);

// --- Șabloane / voci / muzică ---
export const getTemplates = (category?: string) =>
  api.get<{ items: Template[] }>('/api/v1/templates', category ? { category } : undefined);
export const getTemplate = (id: string) => api.get<Template>(`/api/v1/templates/${id}`);
export const getVoices = () => api.get<{ items: Voice[] }>('/api/v1/voices');
export const getCharacters = (ids?: string[]) =>
  api.get<{ items: Character[] }>(
    '/api/v1/characters',
    ids && ids.length > 0 ? { ids: ids.join(',') } : undefined,
  );
export const getMusic = (mood?: string) =>
  api.get<{ items: MusicTrack[] }>('/api/v1/music', mood ? { mood } : undefined);

// --- Clipuri ---
export interface CreateClipBody {
  templateId?: string;
  workflowId?: string;
  category: string;
  tier: string;
  format: string;
  brief?: string;
  title?: string;
  voiceId?: string;
  musicTrackId?: string;
  /**
   * `tts` | `tts_fit_video` | `native` | `native_voice_change` | `lip_sync`
   */
  audioMode?: string;
  campaignId?: string;
  productId?: string;
  /** Imagine de profil / avatar ales din șablon (legacy). */
  avatarFileId?: string;
  /** Personaj din catalog (set de poze image-to-video). */
  characterId?: string;
}
export const createClip = (body: CreateClipBody) =>
  api.post<{ clip: Clip; balance: { counter: string; balance: number; sufficient: boolean } }>(
    '/api/v1/clips',
    body,
  );
export const getClips = (query?: {
  status?: string;
  category?: string;
  tier?: string;
  campaignId?: string;
  page?: number;
  pageSize?: number;
}) => api.get<Paginated<Clip>>('/api/v1/clips', query);
export const getClip = (id: string) => api.get<ClipDetails>(`/api/v1/clips/${id}`);
export const approveScript = (id: string) => api.post<{ clip: Clip }>(`/api/v1/clips/${id}/approve-script`);
export const approveImages = (id: string) => api.post<{ clip: Clip }>(`/api/v1/clips/${id}/approve-images`);
export const regenerateStep = (id: string, step: 'scenario' | 'images' | 'video') =>
  api.post<{ clip: Clip }>(`/api/v1/clips/${id}/regenerate/${step}`);
export const acceptClip = (id: string) => api.post<{ clip: Clip }>(`/api/v1/clips/${id}/accept`);
export const downloadClip = (id: string, kind: 'preview' | 'final' = 'final') =>
  api.get<{ url: string; expiresIn: number; kind: string }>(`/api/v1/clips/${id}/download`, { kind });
export const suggestPronunciation = (
  id: string,
  body: { word: string; segmentIndex: number; context?: string },
) => api.post<PronunciationSuggestResponse>(`/api/v1/clips/${id}/pronunciation/suggest`, body);
export const applyPronunciation = (id: string, body: { segmentIndex: number; respelling: string }) =>
  api.post<{ clip: Clip }>(`/api/v1/clips/${id}/pronunciation/apply`, body);
// Constatare (a): persistă textul unui segment înainte de aprobarea scenariului.
export const editSegment = (id: string, index: number, text: string) =>
  api.patch<{ clip: Clip; segmentIndex: number }>(`/api/v1/clips/${id}/scenario/segments/${index}`, { text });
// Constatare (c): setează respelling-ul pe segment înainte de generarea audio (fără regen).
export const setPronunciation = (id: string, body: { segmentIndex: number; respelling: string }) =>
  api.post<{ clip: Clip }>(`/api/v1/clips/${id}/pronunciation/set`, body);

// --- Share / public ---
export const getShareLinks = (clipId: string) =>
  api.get<{ items: ShareLink[] }>(`/api/v1/clips/${clipId}/share`);
export const createShareLink = (clipId: string, expiresInDays: number) =>
  api.post<ShareLink>(`/api/v1/clips/${clipId}/share`, { expiresInDays });
export const revokeShareLink = (clipId: string, linkId: string) =>
  api.del(`/api/v1/clips/${clipId}/share/${linkId}`);
export const getPublicClip = (token: string) => api.get<PublicClip>(`/api/v1/public/clips/${token}`);

// --- Galerie fișiere ---
export const getFiles = (query?: { kind?: string; page?: number; pageSize?: number }) =>
  api.get<Paginated<FileItem>>('/api/v1/files', query);
export const presignFile = (body: { filename: string; mime: string; size: number }) =>
  api.post<{ fileId: string; key: string; uploadUrl: string; method: string; expiresIn: number; mime: string; kind: string }>(
    '/api/v1/files/presign',
    body,
  );
export const confirmFile = (id: string, body?: Record<string, unknown>) =>
  api.post<FileItem>(`/api/v1/files/${id}/confirm`, body ?? {});
export const getFileUrl = (id: string) => api.get<{ url: string; expiresIn: number }>(`/api/v1/files/${id}/url`);
export const deleteFile = (id: string) => api.del(`/api/v1/files/${id}`);

// --- Produse ---
export const getProducts = (query?: { page?: number; pageSize?: number }) =>
  api.get<Paginated<Product>>('/api/v1/products', query);
export const getProduct = (id: string) => api.get<Product>(`/api/v1/products/${id}`);
export const createProduct = (body: Partial<Product>) => api.post<Product>('/api/v1/products', body);
export const updateProduct = (id: string, body: Partial<Product>) =>
  api.put<Product>(`/api/v1/products/${id}`, body);
export const deleteProduct = (id: string) => api.del(`/api/v1/products/${id}`);
export const importProductUrl = (url: string, opts?: { explore?: boolean }) =>
  api.post<{ product?: Product; explore?: boolean; products?: Product[]; job: { queued: boolean } }>(
    '/api/v1/products/import-url',
    { url, explore: opts?.explore === true },
  );

// --- Campanii ---
export const getCampaigns = (query?: { status?: string; page?: number; pageSize?: number }) =>
  api.get<Paginated<Campaign>>('/api/v1/campaigns', query);
export const createCampaign = (body: Partial<Campaign>) => api.post<Campaign>('/api/v1/campaigns', body);
export const updateCampaign = (id: string, body: Partial<Campaign>) =>
  api.put<Campaign>(`/api/v1/campaigns/${id}`, body);
export const deleteCampaign = (id: string) => api.del(`/api/v1/campaigns/${id}`);

// --- Notificări ---
export const getNotifications = (query?: { unread?: boolean; page?: number; pageSize?: number }) =>
  api.get<Paginated<NotificationItem> & { unread: number }>('/api/v1/me/notifications', {
    unread: query?.unread ? 'true' : undefined,
    page: query?.page,
    pageSize: query?.pageSize,
  });
export const getNotificationUnread = () => api.get<{ unread: number }>('/api/v1/me/notifications/unread-count');
export const markNotificationRead = (id: string) => api.post(`/api/v1/me/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.post('/api/v1/me/notifications/read-all', {});

// --- Suport ---
export const getSupportThreads = () =>
  api.get<{ items: SupportThread[]; unread: number }>('/api/v1/support/threads');
export const getSupportThread = (id: string) =>
  api.get<{ thread: SupportThread; messages: SupportMessage[] }>(`/api/v1/support/threads/${id}`);
export const createSupportThread = (body: {
  subject: string;
  subjectKind: string;
  refId?: string;
  message: string;
  fileIds?: string[];
}) => api.post<{ thread: SupportThread; messages: SupportMessage[] }>('/api/v1/support/threads', body);
export const replySupport = (id: string, body: { body: string; fileIds?: string[] }) =>
  api.post<SupportMessage>(`/api/v1/support/threads/${id}/messages`, body);
export const markSupportRead = (id: string) => api.post<SupportThread>(`/api/v1/support/threads/${id}/read`);

// --- Interviu (ușa 2) ---
export const startInterview = (body: { category: string; tier: string; format: string; productId?: string; campaignId?: string }) =>
  api.post<InterviewSession>('/api/v1/interview/start', body);
export const getInterview = (id: string) => api.get<InterviewSession>(`/api/v1/interview/${id}`);
export const answerInterview = (id: string, text: string) =>
  api.post<{ session: InterviewSession; done: boolean }>(`/api/v1/interview/${id}/answer`, { text });
export const createInterviewClip = (
  id: string,
  body: {
    templateId?: string;
    workflowId?: string;
    voiceId?: string;
    characterId?: string;
    musicTrackId?: string;
  },
) => api.post<{ clip: Clip }>(`/api/v1/interview/${id}/create-clip`, body);

// --- Ultra ---
export const submitUltraBrief = (
  clipId: string,
  body: { brief: string; requirements?: Record<string, unknown>; referenceFileIds?: string[] },
) => api.post<UltraJob>(`/api/v1/clips/${clipId}/ultra-brief`, body);
export const getUltraJobs = () => api.get<{ items: UltraJob[] }>('/api/v1/ultra');
export const getUltraJob = (jobId: string) => api.get<UltraJob>(`/api/v1/ultra/${jobId}`);
export const acceptUltra = (jobId: string) => api.post<UltraJob>(`/api/v1/ultra/${jobId}/accept`, {});
export const requestUltraRevision = (jobId: string, reason: string) =>
  api.post<UltraJob>(`/api/v1/ultra/${jobId}/revision`, { reason });
export const getUltraMessages = (jobId: string) =>
  api.get<{ items: UltraMessage[] }>(`/api/v1/ultra/${jobId}/messages`);
export const postUltraMessage = (jobId: string, body: { body: string; fileIds?: string[] }) =>
  api.post<UltraMessage>(`/api/v1/ultra/${jobId}/messages`, body);

// --- Dev / e2e (montate doar în development; simulează plata Stripe) ---
export const devSimulatePayment = (orderId: string) =>
  api.post<{ orderId: string; status: string; credited: boolean }>('/api/v1/dev/simulate-payment', { orderId });

// --- GDPR ---
export const gdprExport = () => api.post<{ requested?: boolean; url?: string; [k: string]: unknown }>(
  '/api/v1/me/gdpr/export',
  {},
);
export const gdprDeleteRequest = (confirmName: string) =>
  api.post<{ scheduledFor?: string; [k: string]: unknown }>('/api/v1/me/gdpr/delete-request', { confirmName });
export const gdprCancelDelete = () => api.del('/api/v1/me/gdpr/delete-request');
